// App-session liveness watchdog: the mid-session sensor.
//
// "App session" = the life of one running app instance (page load -> renderer
// death/reload), NOT the shopper `sessionId`. The inline boot watchdog in
// index.html only checks `#root` once at ~8s and then disarms, so anything that
// goes wrong AFTER a healthy render is invisible to it - the mid-session blue
// blank / white freeze we've seen in the field leaves no telemetry at all.
//
// This runs for the WHOLE app session (started once the app has rendered) and
// watches three things every tick:
//   - blank : `#root` is empty while the tab is visible (the blue-background
//             state - React cleared/unmounted the tree)
//   - paint : timers fire on time but zero animation frames elapsed while
//             visible (the compositor/GPU wedged with the DOM still intact -
//             what a DOM-only check would miss)
//   - stall : real time since the last tick far exceeds the interval (the main
//             thread was blocked; measured retroactively on recovery)
// A bad state must persist past a debounce before it's reported, so normal
// route transitions / reloads don't create noise. Reports go out live via the
// logger AND are stashed to localStorage for delivery on the next boot (so a
// freeze that never recovers is still reported once the device restarts).
import { Logger } from "@touchtech/web-logging";

import { LocalStorageKeys } from "@is/constants";
import { LocalStorageService } from "@is/services";

export type LivenessPhase = "healthy" | "down";

export type LivenessState = {
  phase: LivenessPhase;
  lastTickAt: number;
  // when the current run of bad readings first appeared (pending the debounce)
  suspectSince: number | null;
  // when the bad state was confirmed (used to measure the blank's duration)
  downSince: number | null;
  // totalVideoFrames of a playing video at the last tick, to detect a decoder
  // that stops advancing (the white screen where JS is alive but nothing plays)
  lastVideoFrames?: number;
};

export type LivenessObservation = {
  now: number;
  rootChildCount: number;
  visible: boolean;
  framesSinceTick: number;
  intervalMs: number;
  // True if the tab was hidden at any point since the previous tick. A hidden
  // tab throttles setInterval to ~1/min, so a late tick then is EXPECTED
  // throttling, not a main-thread freeze - suppress the stall in that case.
  hiddenSinceLastTick?: boolean;
  // totalVideoFrames of a video that is actively PLAYING (not paused/ended),
  // or undefined if nothing is playing. Compared tick-to-tick to spot a frozen
  // decoder. Only playing videos count, so a paused/absent video never trips it.
  playingVideoFrames?: number;
};

export type LivenessConfig = {
  debounceMs: number;
  stallThresholdMs: number;
};

export type LivenessEvent =
  | { type: "stall"; at: number; durationMs: number; visible?: boolean }
  | {
      type: "down";
      at: number;
      reason: "blank" | "paint" | "videostall";
      rootChildCount: number;
      sinceMs: number;
      visible?: boolean;
    }
  | { type: "recovered"; at: number; downMs: number; visible?: boolean };

export const DEFAULT_INTERVAL_MS = 5000;
export const DEFAULT_CONFIG: LivenessConfig = {
  debounceMs: 15000,
  stallThresholdMs: 8000,
};

// Pure. Folds one observation into the running state and returns any events the
// transition produced. All the detection logic lives here so it can be tested
// without timers or a DOM.
export function evaluateLiveness(
  prev: LivenessState,
  obs: LivenessObservation,
  config: LivenessConfig
): { state: LivenessState; events: LivenessEvent[] } {
  const events: LivenessEvent[] = [];

  // Main-thread stall: the tick itself arrived far later than scheduled, so the
  // thread was blocked in between. Reported once, on the tick that recovers.
  // Suppressed if the tab was hidden across the gap - a hidden tab throttles
  // timers to ~1/min, which is NOT a freeze (that was ~all of the field noise).
  const elapsed = obs.now - prev.lastTickAt;
  const timersOnTime = elapsed - obs.intervalMs < config.stallThresholdMs;
  if (!timersOnTime && obs.visible && !obs.hiddenSinceLastTick) {
    events.push({
      type: "stall",
      at: obs.now,
      durationMs: elapsed,
      visible: obs.visible,
    });
  }

  // Bad state only means anything while the tab is visible - a hidden tab is
  // expected to be blank and unpainted (throttled).
  const blank = obs.visible && obs.rootChildCount === 0;
  // Paint stall: timers kept time but no animation frames elapsed -> the
  // compositor stalled even though the DOM is intact. (If timers were ALSO late
  // it's a main-thread stall, already covered above, so require timersOnTime.)
  const paintStall = obs.visible && timersOnTime && obs.framesSinceTick === 0;
  // Video stall: a PLAYING video whose totalVideoFrames isn't advancing while
  // visible = the decoder/display froze even though rAF still fires (the white
  // screen where JS is alive but nothing decodes - store-reported class we were
  // blind to). Needs a prior baseline; a paused/absent video is `undefined` and
  // never trips it. Suppressed when hidden (a hidden tab throttles video too).
  const videoStalled =
    obs.visible &&
    !obs.hiddenSinceLastTick &&
    obs.playingVideoFrames != null &&
    prev.lastVideoFrames != null &&
    obs.playingVideoFrames <= prev.lastVideoFrames;
  const reason: "blank" | "paint" | "videostall" | null = blank
    ? "blank"
    : paintStall
    ? "paint"
    : videoStalled
    ? "videostall"
    : null;
  const isBad = reason !== null;

  let { phase, suspectSince, downSince } = prev;

  if (phase === "healthy") {
    if (isBad) {
      if (suspectSince === null) suspectSince = obs.now;
      if (obs.now - suspectSince >= config.debounceMs) {
        phase = "down";
        downSince = suspectSince;
        events.push({
          type: "down",
          at: obs.now,
          reason,
          rootChildCount: obs.rootChildCount,
          sinceMs: obs.now - downSince,
          visible: obs.visible,
        });
        suspectSince = null;
      }
    } else {
      suspectSince = null;
    }
  } else if (!isBad) {
    events.push({
      type: "recovered",
      at: obs.now,
      downMs: obs.now - (downSince ?? obs.now),
      visible: obs.visible,
    });
    phase = "healthy";
    downSince = null;
    suspectSince = null;
  }

  return {
    state: {
      phase,
      lastTickAt: obs.now,
      suspectSince,
      downSince,
      lastVideoFrames: obs.playingVideoFrames,
    },
    events,
  };
}

// ---- reporting -------------------------------------------------------------

function readItem(key: LocalStorageKeys): string | undefined {
  try {
    const value = LocalStorageService.getItem(key);
    return typeof value === "string" && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function readBuildCommit(): string | undefined {
  try {
    const w = window as unknown as { __buildCommit?: string };
    return typeof w.__buildCommit === "string" ? w.__buildCommit : undefined;
  } catch {
    return undefined;
  }
}

// Report one liveness event: live via the logger (for self-recovering blips
// that never reboot) AND stashed to localStorage (so a freeze that needs a
// manual kill is still delivered on the next boot). Both paths are guarded -
// telemetry must never break the app.
export function reportLivenessEvent(event: LivenessEvent): void {
  const payload = {
    "tenant-id": readItem(LocalStorageKeys.TenantId),
    "device-id": readItem(LocalStorageKeys.DeviceId),
    "store-id": readItem(LocalStorageKeys.StoreId),
    "session-id": readItem(LocalStorageKeys.SessionId),
    buildCommit: readBuildCommit(),
    ...event,
  };

  try {
    Logger.instance.info("App session liveness", payload);
  } catch {
    // live delivery is best-effort
  }

  try {
    const raw = LocalStorageService.getItem(
      LocalStorageKeys.AppSessionLiveness
    );
    const buffer = raw ? JSON.parse(raw) : [];
    const next = (Array.isArray(buffer) ? buffer : []).concat(event).slice(-10);
    LocalStorageService.setItem(
      LocalStorageKeys.AppSessionLiveness,
      JSON.stringify(next)
    );
  } catch {
    // stash is best-effort
  }
}

// Read + clear the stashed liveness events, for delivery on the next boot's
// "App start". Returns undefined when there's nothing buffered.
export function drainAppSessionLiveness(): LivenessEvent[] | undefined {
  let raw: string | null = null;
  try {
    raw = LocalStorageService.getItem(LocalStorageKeys.AppSessionLiveness);
    LocalStorageService.removeItem(LocalStorageKeys.AppSessionLiveness);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : undefined;
  } catch {
    return undefined;
  }
}

// ---- runtime wiring --------------------------------------------------------

// totalVideoFrames of a video that is actively PLAYING (not paused/ended, past
// HAVE_CURRENT_DATA), max across videos - or undefined if nothing is playing.
// Only playing videos count, so a paused/absent/not-started video is undefined
// and never trips the video-stall check.
export function readPlayingVideoFrames(): number | undefined {
  try {
    const videos = Array.from(document.querySelectorAll("video"));
    let best: number | undefined;
    for (const v of videos) {
      const el = v as unknown as {
        paused?: boolean;
        ended?: boolean;
        readyState?: number;
        getVideoPlaybackQuality?: () => { totalVideoFrames?: number };
      };
      if (el.paused || el.ended || (el.readyState ?? 0) < 2) continue;
      if (typeof el.getVideoPlaybackQuality !== "function") continue;
      const total = el.getVideoPlaybackQuality().totalVideoFrames;
      if (typeof total === "number") {
        best = best == null ? total : Math.max(best, total);
      }
    }
    return best;
  } catch {
    return undefined;
  }
}

let livenessTimer: ReturnType<typeof setInterval> | undefined;

// Begin watching the current app session. Call once the app has rendered
// (markAppStartCompleted). Idempotent. Drives its own requestAnimationFrame
// counter so a compositor stall is visible even on a static screen.
export function startAppSessionLiveness(
  intervalMs: number = DEFAULT_INTERVAL_MS,
  config: LivenessConfig = DEFAULT_CONFIG
): void {
  try {
    if (livenessTimer) return;

    let state: LivenessState = {
      phase: "healthy",
      lastTickAt: Date.now(),
      suspectSince: null,
      downSince: null,
    };

    // One-shot rAF probe per tick: requesting a frame forces the compositor to
    // produce one, so on a healthy (even static) screen the callback fires
    // before the next tick. If it never fires while visible, the compositor is
    // wedged. One-shot (not self-rescheduling) so it can't leak in the runner.
    let framesSinceTick = 0;
    const armFrame = () => {
      try {
        requestAnimationFrame(() => {
          framesSinceTick++;
        });
      } catch {
        // no rAF (e.g. non-browser) - paint-stall detection simply won't fire
      }
    };
    armFrame();

    // Track whether the tab was hidden at any point since the last tick, so a
    // throttled (~1/min) tick from a backgrounded tab isn't mistaken for a
    // main-thread freeze. Seeded from the current state; the listener catches
    // transitions (which still fire while the thread is alive).
    let sawHidden = document.visibilityState !== "visible";
    try {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") sawHidden = true;
      });
    } catch {
      // no document/listener - hiddenSinceLastTick stays whatever it seeded
    }

    livenessTimer = setInterval(() => {
      try {
        const root = document.getElementById("root");
        const visible = document.visibilityState === "visible";
        const obs: LivenessObservation = {
          now: Date.now(),
          rootChildCount: root ? root.childElementCount : 1,
          visible,
          framesSinceTick,
          intervalMs,
          hiddenSinceLastTick: sawHidden || !visible,
          playingVideoFrames: readPlayingVideoFrames(),
        };
        sawHidden = !visible; // reset; stay flagged while still hidden
        framesSinceTick = 0;
        armFrame();
        const result = evaluateLiveness(state, obs, config);
        state = result.state;
        result.events.forEach(reportLivenessEvent);
      } catch {
        // a bad tick must never throw out of the interval
      }
    }, intervalMs);

    // Don't keep a Node process (test runner) alive; no-op in the browser.
    (livenessTimer as unknown as { unref?: () => void }).unref?.();
  } catch {
    // best-effort; never break the app
  }
}
